import { useEffect, useState } from 'react';
import { api } from '../lib/api';

type FeedbackEntry = {
  id: string;
  userId: string | null;
  category: string;
  screen: string | null;
  comment: string;
  flaggedFields: string[] | null;
  fieldValues: Record<string, string> | null;
  rawInput: string | null;
  metadata: Record<string, string> | null;
  aiConversation: unknown | null;
  status: string;
  createdAt: string;
  userEmail: string | null;
  attachmentCount: number;
};

type Attachment = {
  id: string;
  feedbackId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
};

const STATUS_OPTIONS = ['new', 'reviewed', 'resolved'] as const;
const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  reviewed: 'bg-yellow-100 text-yellow-700',
  resolved: 'bg-green-100 text-green-700',
};

const CATEGORY_COLORS: Record<string, string> = {
  bug: 'bg-red-100 text-red-700',
  ui: 'bg-purple-100 text-purple-700',
  feature: 'bg-indigo-100 text-indigo-700',
  parsing: 'bg-orange-100 text-orange-700',
  performance: 'bg-yellow-100 text-yellow-700',
  other: 'bg-gray-100 text-gray-700',
};

export default function Feedback() {
  const [entries, setEntries] = useState<FeedbackEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Record<string, Attachment[]>>({});
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getFeedback({
        status: statusFilter || undefined,
        limit: 100,
      });
      setEntries(res.entries);
      setTotal(res.total);
    } catch (err) {
      console.error('Failed to load feedback:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await api.updateFeedbackStatus(id, newStatus);
      setEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, status: newStatus } : e)),
      );
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this feedback entry and all its attachments? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await api.deleteFeedback(id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      setTotal((t) => t - 1);
      if (expandedId === id) setExpandedId(null);
    } catch (err) {
      console.error('Failed to delete feedback:', err);
      alert('Failed to delete. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleExpand = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!attachments[id]) {
      try {
        const res = await api.getFeedbackAttachments(id);
        setAttachments((prev) => ({ ...prev, [id]: res.attachments }));
      } catch {
        setAttachments((prev) => ({ ...prev, [id]: [] }));
      }
    }
  };

  return (
    <div>
      {/* Image lightbox modal */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            className="absolute top-4 right-4 text-white text-3xl leading-none hover:text-gray-300"
            onClick={() => setLightboxUrl(null)}
            aria-label="Close"
          >
            ✕
          </button>
          <img
            src={lightboxUrl}
            alt="Attachment preview"
            className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Feedback</h1>
        <span className="text-sm text-gray-500">{total} total</span>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setStatusFilter('')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
            !statusFilter ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${
              statusFilter === s ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="text-gray-500">No feedback entries found.</p>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => (
            <div key={e.id} className="bg-white rounded-xl border shadow-sm">
              {/* Summary row */}
              <div className="px-5 py-4 flex items-start gap-4">
                <button
                  onClick={() => toggleExpand(e.id)}
                  className="flex-1 text-left min-w-0"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${CATEGORY_COLORS[e.category] ?? 'bg-gray-100 text-gray-700'}`}>
                      {e.category}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[e.status] ?? 'bg-gray-100'}`}>
                      {e.status}
                    </span>
                    {e.attachmentCount > 0 && (
                      <span className="text-xs text-gray-400">📎 {e.attachmentCount}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-900 line-clamp-2">{e.comment}</p>
                  <div className="flex gap-3 mt-1 text-xs text-gray-400">
                    <span>{new Date(e.createdAt).toLocaleString()}</span>
                    {e.userEmail && <span>{e.userEmail}</span>}
                    {e.screen && <span>📱 {e.screen}</span>}
                  </div>
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleDelete(e.id)}
                    disabled={deletingId === e.id}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-40"
                    aria-label="Delete feedback"
                    title="Delete"
                  >
                    {deletingId === e.id ? '…' : '🗑'}
                  </button>
                  <button onClick={() => toggleExpand(e.id)} className="text-gray-400 text-lg">
                    {expandedId === e.id ? '▾' : '▸'}
                  </button>
                </div>
              </div>

              {/* Expanded detail */}
              {expandedId === e.id && (
                <div className="border-t px-5 py-4 space-y-4">
                  {/* Status selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Status:</span>
                    {STATUS_OPTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => handleStatusChange(e.id, s)}
                        className={`px-3 py-1 rounded text-xs font-medium capitalize ${
                          e.status === s
                            ? STATUS_COLORS[s]
                            : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>

                  {/* Full comment */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Comment</h4>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{e.comment}</p>
                  </div>

                  {/* Flagged fields */}
                  {e.flaggedFields != null && e.flaggedFields.length > 0 ? (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Flagged Fields</h4>
                      <div className="flex flex-wrap gap-1">
                        {e.flaggedFields.map((f) => (
                          <span key={f} className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded">
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/* Field values */}
                  {e.fieldValues != null && Object.keys(e.fieldValues).length > 0 ? (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Parsed Values</h4>
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        {Object.entries(e.fieldValues).map(([k, v]) => (
                          <div key={k} className="contents">
                            <dt className="text-gray-500">{k}</dt>
                            <dd className="text-gray-800">{String(v)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ) : null}

                  {/* Raw input */}
                  {e.rawInput && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Original Input</h4>
                      <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded whitespace-pre-wrap">{e.rawInput}</p>
                    </div>
                  )}

                  {/* Metadata */}
                  {e.metadata && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Device Info</h4>
                      <div className="text-xs text-gray-500 space-x-3">
                        {Object.entries(e.metadata).map(([k, v]) => (
                          <span key={k}><strong>{k}:</strong> {String(v)}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Attachments */}
                  {(attachments[e.id]?.length ?? 0) > 0 ? (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Attachments</h4>
                      <div className="flex flex-wrap gap-3">
                        {attachments[e.id]!.map((att) => (
                          att.mimeType.startsWith('image/') ? (
                            <button
                              key={att.id}
                              onClick={() => setLightboxUrl(api.getFeedbackAttachmentUrl(e.id, att.id))}
                              className="block focus:outline-none focus:ring-2 focus:ring-indigo-400 rounded-lg"
                              aria-label={`View ${att.filename}`}
                            >
                              <img
                                src={api.getFeedbackAttachmentUrl(e.id, att.id)}
                                alt={att.filename}
                                className="w-40 h-auto rounded-lg border hover:shadow-md hover:scale-105 transition-all cursor-zoom-in"
                              />
                            </button>
                          ) : (
                            <a
                              key={att.id}
                              href={api.getFeedbackAttachmentUrl(e.id, att.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <div className="px-3 py-2 bg-gray-50 rounded-lg border text-sm text-gray-600 hover:bg-gray-100">
                                📄 {att.filename} ({(att.sizeBytes / 1024).toFixed(0)} KB)
                              </div>
                            </a>
                          )
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/* AI Conversation */}
                  {e.aiConversation != null && Array.isArray(e.aiConversation) && e.aiConversation.length > 0 ? (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">AI Conversation</h4>
                      <div className="space-y-2 text-sm">
                        {(e.aiConversation as { userMessage: string; aiResponse: string; model: string }[]).map((log, i) => (
                          <div key={i} className="bg-gray-50 p-2 rounded">
                            <p className="text-gray-500 text-xs mb-1">{log.model}</p>
                            <p><strong>User:</strong> {log.userMessage}</p>
                            <p className="mt-1"><strong>AI:</strong> {log.aiResponse.slice(0, 500)}{log.aiResponse.length > 500 ? '…' : ''}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
